import React, { useEffect } from 'react';
import { Form, Input, Select, Row, Col, App } from 'antd';
import { trpc } from '../../lib/api';
import { katakanaRule, phoneNumberRule, postalCodeRule } from '@/constants/validation';
import { withDevModalFilePath, SingleUserSelect, PhoneInput, PostalCodeInput, EmailInput, RichTextEditor } from '../common';
import { useFormAutoBackup } from '../../hooks/useFormAutoBackup';

const { Modal } = withDevModalFilePath('client/src/components/suppliers/SupplierFormModal.tsx');

interface Supplier {
  id?: string;
  name?: string | null;
  nameKana?: string | null;
  postalCode?: string | null;
  address?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
  email?: string | null;
  contactPerson?: string | null;
  managerId?: string | null;
  supplierCategoryId?: string | null;
  category?: {
    id?: string;
    categoryLabel?: string;
    color?: string | null;
    icon?: string | null;
  } | null;
  notes?: string | null;
}

interface SupplierFormModalProps {
  open: boolean;
  supplier: Supplier | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface SupplierFormValues extends Record<string, unknown> {
  name: string;
  nameKana?: string;
  postalCode?: string;
  address?: string;
  phone?: string;
  mobilePhone?: string;
  fax?: string;
  email?: string;
  contactPerson?: string;
  supplierCategoryId?: string;
  managerId?: string;
  notes?: string;
}

export const SupplierFormModal: React.FC<SupplierFormModalProps> = ({
  open,
  supplier,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<SupplierFormValues>();
  const { message } = App.useApp();

  // 編集モードかどうか
  const isEditMode = !!supplier;

  // フォーム自動バックアップ（新規作成のみ有効）
  const { hasBackup, showRestoreConfirm, clearBackup } = useFormAutoBackup({    
    formType: 'supplier',
    entityId: undefined, // 新規作成のみなのでIDは不要
    form,
    enabled: open && !isEditMode,
  });

  // 業者カテゴリ一覧取得
  const { data: supplierCategories = [] } = trpc.supplierCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  // 作成mutation
  const createMutation = trpc.suppliers.create.useMutation({
    onSuccess: () => {
      message.success('業者を登録しました');
      form.resetFields();
      clearBackup(); // 保存成功時にバックアップをクリア
      onSuccess();
    },
    onError: (error) => {
      message.error(`登録に失敗しました: ${error.message}`);
    },
  });

  // 更新mutation
  const updateMutation = trpc.suppliers.update.useMutation({
    onSuccess: () => {
      message.success('業者を更新しました');
      clearBackup(); // 保存成功時にバックアップをクリア
      onSuccess();
    },
    onError: (error) => {
      message.error(`更新に失敗しました: ${error.message}`);
    },
  });

  // フォーム初期値設定
  useEffect(() => {
    if (open) {
      if (supplier) {
        // 編集モード
        form.setFieldsValue({
          name: supplier.name ?? '',
          nameKana: supplier.nameKana || undefined,
          postalCode: supplier.postalCode || undefined,
          address: supplier.address || undefined,
          phone: supplier.phone || undefined,
          mobilePhone: supplier.mobilePhone || undefined,
          fax: supplier.fax || undefined,
          email: supplier.email || undefined,
          contactPerson: supplier.contactPerson || undefined,
          supplierCategoryId: supplier.supplierCategoryId ?? supplier.category?.id ?? undefined,
          managerId: supplier.managerId || undefined,
          notes: supplier.notes || undefined,
        });
      } else {
        // 新規作成モード
        form.resetFields();
      }
    }
  }, [open, supplier, form]);

  // モーダルが開かれた時にバックアップの確認を表示
  useEffect(() => {
    if (open && hasBackup) {
      const timer = setTimeout(() => {
        showRestoreConfirm();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, hasBackup, showRestoreConfirm]);

  // 送信処理
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (supplier?.id) {
        // 更新
        updateMutation.mutate({
          id: supplier.id,
          ...values,
        });
      } else {
        // 作成
        createMutation.mutate(values);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={supplier ? '業者編集' : '新規業者登録'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={createMutation.isPending || updateMutation.isPending}
      width={800}
      okText={supplier ? '更新' : '登録'}
      cancelText="キャンセル"
    >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            {/* 基本情報 */}
            <Col span={24}>
              <h3>基本情報</h3>
            </Col>

            <Col span={12}>
              <Form.Item
                label="業者名"
                name="name"
                rules={[{ required: true, message: '業者名を入力してください' }]}
              >
                <Input placeholder="例: 株式会社山田商事" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="フリガナ"
                name="nameKana"
                rules={[
                  { max: 255, message: '255文字以内で入力してください' },
                  katakanaRule(),
                ]}
              >
                <Input placeholder="例: ヤマダショウジ" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="カテゴリ"
                name="supplierCategoryId"
                rules={[{ required: true, message: 'カテゴリを選択してください' }]}
              >
                <Select
                  placeholder="カテゴリを選択"
                  allowClear
                  options={supplierCategories.map(cat => ({
                    label: cat.categoryLabel,
                    value: cat.id,
                  }))}
                />
              </Form.Item>
            </Col>

            {/* 住所情報 */}
            <Col span={24}>
              <h3 style={{ marginTop: 16 }}>住所情報</h3>
            </Col>

            <Col span={8}>
              <Form.Item
                label="郵便番号"
                name="postalCode"
                rules={[postalCodeRule()]}
              >
                <PostalCodeInput placeholder="例: 123-4567" />
              </Form.Item>
            </Col>

            <Col span={16}>
              <Form.Item label="住所" name="address">
                <Input placeholder="例: 東京都千代田区..." />
              </Form.Item>
            </Col>

            {/* 連絡先情報 */}
            <Col span={24}>
              <h3 style={{ marginTop: 16 }}>連絡先情報</h3>
            </Col>

            <Col span={8}>
              <Form.Item
                label="電話番号"
                name="phone"
                rules={[
                  { max: 20, message: '電話番号は20文字以内で入力してください' },
                  phoneNumberRule(),
                ]}
              >
                <PhoneInput placeholder="例: 03-1234-5678" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="携帯電話"
                name="mobilePhone"
                rules={[
                  { max: 20, message: '携帯電話番号は20文字以内で入力してください' },
                  phoneNumberRule('有効な携帯電話番号を入力してください'),
                ]}
              >
                <PhoneInput placeholder="例: 090-1234-5678" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="FAX番号"
                name="fax"
                rules={[
                  { max: 20, message: 'FAX番号は20文字以内で入力してください' },
                  phoneNumberRule('有効なFAX番号を入力してください'),
                ]}
              >
                <PhoneInput placeholder="例: 03-1234-5679" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="メールアドレス"
                name="email"
                rules={[
                  { type: 'email', message: '正しいメールアドレスを入力してください' },
                ]}
              >
                <EmailInput placeholder="例: info@example.com" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="担当者名" name="contactPerson">
                <Input placeholder="例: 山田太郎" />
              </Form.Item>
            </Col>

            {/* 管理情報 */}
            <Col span={24}>
              <h3 style={{ marginTop: 16 }}>管理情報</h3>
            </Col>

            <Col span={12}>
              <Form.Item label="担当ユーザー" name="managerId">
                <SingleUserSelect
                  placeholder="担当ユーザーを選択"
                  showAssignMeButton
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label="詳細"
                name="notes"
                getValueFromEvent={(markdown: string) => markdown}
                getValueProps={(value) => ({ value: value || '' })}
              >
                <RichTextEditor
                  placeholder="詳細を入力してください..."
                  minHeight={120}
                  showPreviewToggle={false}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
  );
};

export default SupplierFormModal;
